<template>
  <div class="server-card">
    <!-- Header -->
    <div class="card-header">
      <div class="header-left">
        <span class="server-name">{{ server.name || 'Unknown' }}</span>
        <span class="server-version">v{{ serverData?.app?.version || server.version || '0.0.0' }}</span>
      </div>
      <div class="header-right">
        <span class="status-dot" :class="statusClass"></span>
        <span class="response-time" v-if="serverData?.response_time_ms">
          {{ serverData.response_time_ms.toFixed(1) }}ms
        </span>
      </div>
    </div>

    <!-- Error State -->
    <div v-if="errorMessage" class="error-message">{{ errorMessage }}</div>

    <!-- Loading State -->
    <div v-else-if="isLoading" class="loader">
      <div class="spinner"></div>
    </div>

    <!-- Content -->
    <div v-else-if="serverData" class="card-content">
      <!-- Protocol Connectivity -->
      <div class="info-row">
        <span class="label">Connection</span>
        <span class="value protocol-statuses">
          <span class="badge" :class="getProtocolClass(protocolChecks.https)">{{ formatProtocolLabel('https', protocolChecks.https) }}</span>
          <span class="badge" :class="getProtocolClass(protocolChecks.http)">{{ formatProtocolLabel('http', protocolChecks.http) }}</span>
        </span>
      </div>

      <!-- System Info Row -->
      <div class="info-row clickable" @click="showSystemPopover = !showSystemPopover">
        <span class="label">{{ serverData.system?.hostname || 'Unknown' }}</span>
        <span class="value muted">{{ serverData.system?.os }} {{ serverData.system?.os_version }}</span>
      </div>
      <Popover v-if="showSystemPopover" @close="showSystemPopover = false" title="System Info">
        <div class="popover-row"><span>CPU:</span><span>{{ serverData.system?.cpu_brand?.trim() }}</span></div>
        <div class="popover-row"><span>Cores:</span><span>{{ serverData.system?.cpu_count }}</span></div>
        <div class="popover-row"><span>Arch:</span><span>{{ serverData.system?.cpu_arch }}</span></div>
        <div class="popover-row"><span>Kernel:</span><span>{{ serverData.system?.kernel_version }}</span></div>
        <div class="popover-row"><span>Timezone:</span><span>{{ serverData.app?.timezone }}</span></div>
        <div class="popover-row"><span>GUID:</span><span class="guid">{{ serverData.app?.guid }}</span></div>
      </Popover>

      <!-- CPU Usage -->
      <div class="metric-row">
        <div class="metric-header">
          <span class="label">CPU <span class="cores-hint">({{ serverData.system?.cpu_count }} cores)</span></span>
          <span class="value">{{ cpuNormalizedPercent.toFixed(1) }}%</span>
        </div>
        <div class="progress-bar">
          <div 
            class="progress-fill" 
            :class="getColorClass(cpuNormalizedPercent)"
            :style="{ width: cpuNormalizedPercent + '%' }"
          ></div>
        </div>
      </div>

      <!-- Memory Usage -->
      <div class="metric-row">
        <div class="metric-header">
          <span class="label">RAM</span>
          <span class="value">{{ formatBytes(serverData.memory?.used_bytes) }} / {{ formatBytes(serverData.memory?.total_bytes) }}</span>
        </div>
        <div class="progress-bar">
          <div 
            class="progress-fill" 
            :class="getColorClass(memoryPercent)"
            :style="{ width: memoryPercent + '%' }"
          ></div>
        </div>
      </div>

      <!-- Disks -->
      <div v-for="disk in disks" :key="disk.mount_point" class="metric-row">
        <div class="metric-header">
          <span class="label">{{ disk.mount_point }} {{ disk.name ? `(${disk.name})` : '' }}</span>
          <span class="value">{{ formatBytes(disk.used_bytes) }} / {{ formatBytes(disk.total_bytes) }}</span>
        </div>
        <div class="progress-bar">
          <div 
            class="progress-fill" 
            :class="getColorClass(disk.usage_percent)"
            :style="{ width: disk.usage_percent + '%' }"
          ></div>
        </div>
      </div>

      <!-- Recorders -->
      <div class="info-row clickable" @click="showRecordersPopover = !showRecordersPopover">
        <span class="label">Recorders</span>
        <span class="value">
          <span class="badge success">{{ serverData.recorders?.running || 0 }}</span>
          <span v-if="serverData.recorders?.stopped" class="badge danger">{{ serverData.recorders.stopped }}</span>
          <span v-if="serverData.recorders?.hung_count" class="badge warning">{{ serverData.recorders.hung_count }} hung</span>
        </span>
      </div>
      <Popover v-if="showRecordersPopover" @close="showRecordersPopover = false" title="Recorders">
        <div class="popover-row"><span>Running:</span><span>{{ serverData.recorders?.running }}</span></div>
        <div class="popover-row"><span>Stopped:</span><span>{{ serverData.recorders?.stopped }}</span></div>
        <div class="popover-row"><span>Total:</span><span>{{ serverData.recorders?.total }}</span></div>
        <div class="popover-row"><span>Total Packets:</span><span>{{ formatNumber(serverData.recorders?.aggregate_stats?.total_packets) }}</span></div>
        <div class="popover-row"><span>Errors:</span><span>{{ serverData.recorders?.aggregate_stats?.total_errors }}</span></div>
        <div class="popover-row"><span>Write Failures:</span><span>{{ serverData.recorders?.aggregate_stats?.total_write_failures }}</span></div>
      </Popover>

      <!-- Process Info -->
      <div class="info-row clickable" @click="showProcessPopover = !showProcessPopover">
        <span class="label">Process</span>
        <span class="value">{{ formatBytes(serverData.process?.memory_bytes) }} · {{ formatUptime(serverData.process?.run_time_seconds) }}</span>
      </div>
      <Popover v-if="showProcessPopover" @close="showProcessPopover = false" title="Process Info">
        <div class="popover-row"><span>PID:</span><span>{{ serverData.process?.pid }}</span></div>
        <div class="popover-row"><span>Memory:</span><span>{{ formatBytes(serverData.process?.memory_bytes) }}</span></div>
        <div class="popover-row"><span>Virtual Memory:</span><span>{{ formatBytes(serverData.process?.virtual_memory_bytes) }}</span></div>
        <div class="popover-row"><span>CPU Usage:</span><span>{{ serverData.process?.cpu_usage_percent?.toFixed(1) }}%</span></div>
        <div class="popover-row"><span>Uptime:</span><span>{{ formatUptime(serverData.process?.run_time_seconds) }}</span></div>
      </Popover>

      <!-- Caches & Jobs Row -->
      <div class="info-row">
        <span class="label clickable" @click="showCachesPopover = !showCachesPopover">
          Caches <span class="badge muted">{{ serverData.caches?.db_connections || 0 }} DB</span>
        </span>
        <span class="label clickable" @click="showJobsPopover = !showJobsPopover">
          Jobs <span class="badge muted">{{ serverData.concat_jobs?.total || 0 }}</span>
        </span>
      </div>
      <Popover v-if="showCachesPopover" @close="showCachesPopover = false" title="Caches">
        <div class="popover-row"><span>DB Connections:</span><span>{{ serverData.caches?.db_connections }}</span></div>
        <div class="popover-row"><span>Intercoms Loaded:</span><span>{{ serverData.caches?.intercoms_loaded }}</span></div>
        <div class="popover-row"><span>Timeline Day Entries:</span><span>{{ serverData.caches?.timeline_day_cache_entries }}</span></div>
        <div class="popover-row"><span>Segment Data Entries:</span><span>{{ serverData.caches?.segment_data_cache_entries }}</span></div>
        <div class="popover-row"><span>TS Cache:</span><span>{{ formatBytes(serverData.caches?.corrected_ts_cache?.total_bytes) }} / {{ formatBytes(serverData.caches?.corrected_ts_cache?.max_bytes) }}</span></div>
      </Popover>
      <Popover v-if="showJobsPopover" @close="showJobsPopover = false" title="Concat Jobs">
        <div class="popover-row"><span>Total:</span><span>{{ serverData.concat_jobs?.total }}</span></div>
        <div class="popover-row"><span>Pending:</span><span>{{ serverData.concat_jobs?.by_status?.pending }}</span></div>
        <div class="popover-row"><span>Running:</span><span>{{ serverData.concat_jobs?.by_status?.running }}</span></div>
        <div class="popover-row"><span>Completed:</span><span>{{ serverData.concat_jobs?.by_status?.completed }}</span></div>
        <div class="popover-row"><span>Failed:</span><span>{{ serverData.concat_jobs?.by_status?.failed }}</span></div>
      </Popover>

      <!-- Bottom Row -->
      <div class="info-row small">
        <span v-if="serverData.peer_discovery?.enabled" class="badge" :class="serverData.peer_discovery?.peer_count ? 'success' : 'muted'">
          {{ serverData.peer_discovery?.peer_count || 0 }} peers
        </span>
        <span v-if="serverData.reverse_proxy?.enabled" class="badge success">proxy</span>
        <span class="badge muted">{{ serverData.notifications?.total_stored || 0 }} notif</span>
        <span class="timestamp">{{ formatTimestamp(serverData.timestamp) }}</span>
      </div>
    </div>
  </div>
</template>

<script>
const Popover = {
  name: 'Popover',
  props: ['title'],
  emits: ['close'],
  template: `
    <Teleport to="body">
      <div class="server-info-popover-overlay" @click.self="$emit('close')">
        <div class="server-info-popover-content">
          <div class="server-info-popover-header">
            <span>{{ title }}</span>
            <button class="server-info-popover-close" @click="$emit('close')">×</button>
          </div>
          <div class="server-info-popover-body">
            <slot></slot>
          </div>
        </div>
      </div>
    </Teleport>
  `
}

export default {
  name: 'ServerInfo',
  components: { Popover },
  props: {
    server: {
      type: Object,
      required: true
    },
    serverState: {
      type: Object,
      default: () => ({})
    }
  },
  data() {
    return {
      showSystemPopover: false,
      showRecordersPopover: false,
      showProcessPopover: false,
      showCachesPopover: false,
      showJobsPopover: false
    }
  },
  computed: {
    serverData() {
      return this.serverState?.data || null
    },
    errorMessage() {
      return this.serverState?.error || null
    },
    isLoading() {
      return Boolean(this.serverState?.loading)
    },
    protocolChecks() {
      return this.serverState?.protocolChecks || {
        https: 'unknown',
        http: 'unknown'
      }
    },
    statusClass() {
      if (this.errorMessage) return 'error'
      if (!this.serverData) return 'unknown'
      return this.serverData.status === 'ok' ? 'ok' : 'error'
    },
    memoryPercent() {
      if (!this.serverData?.memory) return 0
      return (this.serverData.memory.used_bytes / this.serverData.memory.total_bytes) * 100
    },
    cpuNormalizedPercent() {
      if (!this.serverData?.cpu_usage_percent || !this.serverData?.system?.cpu_count) return 0
      // CPU usage is combined across all cores (e.g., 800% max for 8 cores)
      // Normalize to 0-100% range
      return this.serverData.cpu_usage_percent / this.serverData.system.cpu_count
    },
    disks() {
      return this.serverData?.disks || []
    }
  },
  methods: {
    getProtocolClass(status) {
      if (status === 'ok') return 'success'
      if (status === 'error') return 'danger'
      if (status === 'checking') return 'warning'
      return 'muted'
    },
    formatProtocolLabel(protocol, status) {
      if (status === 'checking') return `${protocol}: ...`
      if (status === 'ok') return `${protocol}: ok`
      if (status === 'error') return `${protocol}: fail`
      return `${protocol}: n/a`
    },
    getColorClass(value) {
      if (value < 50) return 'success'
      if (value < 75) return 'warning'
      return 'danger'
    },
    formatBytes(bytes) {
      if (!bytes) return '0 B'
      const units = ['B', 'KB', 'MB', 'GB', 'TB']
      let i = 0
      while (bytes >= 1024 && i < units.length - 1) {
        bytes /= 1024
        i++
      }
      return `${bytes.toFixed(1)} ${units[i]}`
    },
    formatNumber(num) {
      if (!num) return '0'
      return num.toLocaleString()
    },
    formatUptime(seconds) {
      if (!seconds) return '0s'
      const days = Math.floor(seconds / 86400)
      const hours = Math.floor((seconds % 86400) / 3600)
      const mins = Math.floor((seconds % 3600) / 60)
      if (days > 0) return `${days}d ${hours}h`
      if (hours > 0) return `${hours}h ${mins}m`
      return `${mins}m`
    },
    formatTimestamp(ts) {
      if (!ts) return ''
      const date = new Date(ts)
      return date.toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit', second: '2-digit' })
    }
  }
}
</script>

<style scoped>
.server-card {
  font-size: 11px;
  background: #1e2228;
  border-radius: 8px;
  padding: 10px 12px;
  color: #e0e0e0;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.3);
  transition: transform 0.2s, box-shadow 0.2s;
  position: relative;
}

.server-card:hover {
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.4);
}

.card-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 8px;
  padding-bottom: 6px;
  border-bottom: 1px solid #2a2f38;
}

.header-left {
  display: flex;
  flex-direction: column;
  gap: 1px;
}

.server-name {
  font-size: 13px;
  font-weight: 600;
  color: #fff;
}

.server-version {
  font-size: 10px;
  color: #888;
}

.header-right {
  display: flex;
  align-items: center;
  gap: 6px;
}

.status-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: #666;
}

.status-dot.ok { background: #4caf50; }
.status-dot.error { background: #f44336; }
.status-dot.unknown { background: #666; }

.response-time {
  font-size: 9px;
  color: #888;
}

.error-message {
  color: #f44336;
  padding: 8px;
  text-align: center;
}

.loader {
  display: flex;
  justify-content: center;
  padding: 20px;
}

.spinner {
  width: 20px;
  height: 20px;
  border: 2px solid #333;
  border-top-color: #4caf50;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

.card-content {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.info-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 2px 0;
}

.info-row.small {
  font-size: 9px;
  gap: 4px;
  flex-wrap: wrap;
}

.clickable {
  cursor: pointer;
  border-radius: 3px;
  padding: 2px 4px;
  margin: -2px -4px;
  transition: background 0.15s;
}

.clickable:hover {
  background: rgba(255, 255, 255, 0.05);
}

.label {
  color: #aaa;
}

.value {
  color: #fff;
  display: flex;
  align-items: center;
  gap: 4px;
}

.value.muted {
  color: #666;
  font-size: 10px;
}

.cores-hint {
  font-size: 9px;
  color: #666;
  font-weight: normal;
}

.metric-row {
  display: flex;
  flex-direction: column;
  gap: 3px;
}

.metric-header {
  display: flex;
  justify-content: space-between;
  font-size: 10px;
}

.progress-bar {
  height: 4px;
  background: #2a2f38;
  border-radius: 2px;
  overflow: hidden;
}

.progress-fill {
  height: 100%;
  border-radius: 2px;
  transition: width 0.3s;
}

.progress-fill.success { background: #4caf50; }
.progress-fill.warning { background: #ff9800; }
.progress-fill.danger { background: #f44336; }

.badge {
  font-size: 9px;
  padding: 1px 5px;
  border-radius: 3px;
  background: #2a2f38;
}

.badge.success { background: #1b4d1b; color: #4caf50; }
.badge.warning { background: #4d3a1b; color: #ff9800; }
.badge.danger { background: #4d1b1b; color: #f44336; }
.badge.muted { background: #252a30; color: #666; }

.protocol-statuses {
  gap: 6px;
}

.timestamp {
  margin-left: auto;
  color: #555;
}
</style>

<!-- Global styles for teleported popover -->
<style>
.server-info-popover-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10000;
}

.server-info-popover-content {
  background: #252a30;
  border-radius: 8px;
  min-width: 280px;
  max-width: 90vw;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.5);
  font-family: inherit;
}

.server-info-popover-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 10px 12px;
  border-bottom: 1px solid #333;
  font-weight: 600;
  font-size: 13px;
  color: #fff;
}

.server-info-popover-close {
  background: none;
  border: none;
  color: #888;
  font-size: 18px;
  cursor: pointer;
  padding: 0;
  line-height: 1;
}

.server-info-popover-close:hover {
  color: #fff;
}

.server-info-popover-body {
  padding: 10px 12px;
}

.server-info-popover-body .popover-row {
  display: flex;
  justify-content: space-between;
  padding: 4px 0;
  font-size: 11px;
  border-bottom: 1px solid #2a2f38;
}

.server-info-popover-body .popover-row:last-child {
  border-bottom: none;
}

.server-info-popover-body .popover-row span:first-child {
  color: #888;
}

.server-info-popover-body .popover-row span:last-child {
  color: #fff;
  text-align: right;
  max-width: 60%;
  word-break: break-word;
}

.server-info-popover-body .guid {
  font-size: 8px;
  word-break: break-all;
  max-width: 180px;
}
</style>
